<template>
	<div class="course-management">
		<div class="course-management__header">
			<h1 class="course-management__title">Управление курсами</h1>
			<button
				class="course-management__add-btn"
				@click="$router.push('/teacher/create-course')"
			>
				<span class="course-management__add-icon">+</span>
				Создать курс
			</button>
		</div>

		<!-- Filters -->
		<div class="course-management__filters">
			<input
				v-model="searchQuery"
				class="course-management__search"
				type="text"
				placeholder="Поиск по названию курса..."
			/>
			<select v-model="categoryFilter" class="course-management__select">
				<option value="">Все категории</option>
				<option value="programming">Программирование</option>
				<option value="design">Дизайн</option>
				<option value="business">Бизнес</option>
				<option value="marketing">Маркетинг</option>
			</select>
			<select v-model="statusFilter" class="course-management__select">
				<option value="">Все статусы</option>
				<option value="draft">Черновик</option>
				<option value="published">Опубликован</option>
				</select>
				<SaveStatusIndicator
					class="course-management__save-status"
					:status="filtersSaveStatus"
					:status-text="filtersSaveStatusText"
					:status-icon="filtersSaveStatusIcon"
					:status-color="filtersSaveStatusColor"
					:last-saved-at="filtersLastSavedAt"
				/>
			</div>

			<!-- Courses Table -->
		<div class="course-management__table">
			<div v-if="loading" class="course-management__loading">
				<div class="spinner"></div>
				<p>Загрузка курсов...</p>
			</div>

			<table v-else class="table">
				<thead>
					<tr>
						<th>ID</th>
						<th>Название</th>
						<th>Категория</th>
						<th>Преподаватель</th>
						<th>Студентов</th>
						<th>Статус</th>
						<th>Дата создания</th>
						<th>Действия</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="course in filteredCourses" :key="course.id">
						<td>{{ course.id }}</td>
						<td>
							<div class="table__course">
								<strong>{{ course.title }}</strong>
								<span v-if="course.subtitle" class="table__course-subtitle">
									{{ course.subtitle }}
								</span>
							</div>
						</td>
						<td>{{ getCourseCategoryLabel(course.category) }}</td>
						<td>{{ course.teacher_name || 'Не указан' }}</td>
						<td>{{ course.students_count || 0 }}</td>
						<td>
							<span
								class="table__status"
								:class="`table__status--${
									course.is_published ? 'published' : 'draft'
								}`"
							>
								{{ course.is_published ? 'Опубликован' : 'Черновик' }}
							</span>
						</td>
						<td>{{ formatDate(course.created_at) }}</td>
						<td>
							<div class="table__actions">
								<button
									class="table__action-btn"
									@click="viewCourse(course)"
									title="Просмотр"
								>
									👁️
								</button>
								<button
									class="table__action-btn"
									@click="editCourse(course)"
									title="Редактировать"
								>
									✏️
								</button>
								<button
									v-if="!course.is_published"
									class="table__action-btn table__action-btn--publish"
									@click="publishCourse(course)"
									title="Опубликовать"
								>
									✅
								</button>
								<button
									v-else
									class="table__action-btn table__action-btn--unpublish"
									@click="unpublishCourse(course)"
									title="Снять с публикации"
								>
									⏸️
								</button>
								<button
									class="table__action-btn table__action-btn--delete"
									@click="deleteCourse(course)"
									:disabled="!canDeleteCourses"
									title="Удалить"
								>
									🗑️
								</button>
							</div>
						</td>
					</tr>
				</tbody>
			</table>
		</div>
	</div>
</template>

<script>
import { ref } from 'vue'
import { coursesService } from '@/services'
import adminRolesService from '@/services/adminRoles'
import { getCourseCategoryLabel } from '@/utils/courseCategories'
import { useAutoSave } from '@/composables/useAutoSave'
import SaveStatusIndicator from '@/components/common/SaveStatusIndicator.vue'

export default {
	name: 'CourseManagement',
	components: {
		SaveStatusIndicator,
	},
	setup() {
		const filtersDraft = ref({
			searchQuery: '',
			categoryFilter: '',
			statusFilter: '',
		})
		const {
			lastSaved: filtersLastSavedAt,
			hasUnsavedChanges: hasUnsavedFilterChanges,
			restoreFromStorage: restoreFiltersDraft,
		} = useAutoSave('admin_course_management_filters', filtersDraft, {
			interval: 800,
		})

		return {
			filtersDraft,
			filtersLastSavedAt,
			hasUnsavedFilterChanges,
			restoreFiltersDraft,
		}
	},
	data() {
		return {
			loading: false,
			searchQuery: '',
			categoryFilter: '',
			statusFilter: '',
			courses: [],
			permissions: [],
		}
	},
	computed: {
		filtersSaveStatus() {
			if (this.hasUnsavedFilterChanges) return 'saving'
			if (this.filtersLastSavedAt) return 'saved'
			return 'idle'
		},
		filtersSaveStatusText() {
			if (this.hasUnsavedFilterChanges) return 'Сохранение фильтров...'
			if (this.filtersLastSavedAt) return 'Фильтры сохранены'
			return ''
		},
		filtersSaveStatusIcon() {
			if (this.hasUnsavedFilterChanges) return 'mdi-loading mdi-spin'
			if (this.filtersLastSavedAt) return 'mdi-check-circle'
			return ''
		},
		filtersSaveStatusColor() {
			if (this.hasUnsavedFilterChanges) return 'info'
			if (this.filtersLastSavedAt) return 'success'
			return ''
		},
		filteredCourses() {
			return this.courses.filter(course => {
				const matchesSearch =
					!this.searchQuery ||
					course.title?.toLowerCase().includes(this.searchQuery.toLowerCase())

				const matchesCategory =
					!this.categoryFilter || course.category === this.categoryFilter

				const matchesStatus =
					!this.statusFilter ||
					(this.statusFilter === 'published' && course.is_published) ||
					(this.statusFilter === 'draft' && !course.is_published)

				return matchesSearch && matchesCategory && matchesStatus
			})
		},
		canDeleteCourses() {
			return (
				this.permissions.includes('*') ||
				this.permissions.includes('delete_courses')
			)
		},
	},
	mounted() {
		const restored = this.restoreFiltersDraft?.()
		if (restored?.data && typeof restored.data === 'object') {
			this.searchQuery = restored.data.searchQuery || ''
			this.categoryFilter = restored.data.categoryFilter || ''
			this.statusFilter = restored.data.statusFilter || ''
		}
		this.syncFiltersDraft()
		this.loadCourses()
		this.loadPermissions()
	},
	watch: {
		searchQuery() {
			this.syncFiltersDraft()
		},
		categoryFilter() {
			this.syncFiltersDraft()
		},
		statusFilter() {
			this.syncFiltersDraft()
		},
	},
	methods: {
		getCourseCategoryLabel,
		syncFiltersDraft() {
			if (!this.filtersDraft) return
			this.filtersDraft.searchQuery = this.searchQuery
			this.filtersDraft.categoryFilter = this.categoryFilter
			this.filtersDraft.statusFilter = this.statusFilter
		},
		async loadPermissions() {
			try {
				const data = await adminRolesService.getMyPermissions()
				this.permissions = data?.permissions || []
			} catch (error) {
				console.error('Ошибка загрузки прав администратора:', error)
				this.permissions = []
			}
		},
		async loadCourses() {
			this.loading = true
			try {
				this.courses = await coursesService.getAll()
			} catch (error) {
				console.error('Ошибка загрузки курсов:', error)
				alert('Не удалось загрузить курсы')
			} finally {
				this.loading = false
			}
		},

		formatDate(date) {
			if (!date) return ''
			return new Date(date).toLocaleDateString('ru-RU')
		},

		viewCourse(course) {
			this.$router.push(`/courses/${course.id}`)
		},

		editCourse(course) {
			this.$router.push(`/teacher/create-course?id=${course.id}`)
		},

		async publishCourse(course) {
			if (!confirm(`Опубликовать курс "${course.title}"?`)) {
				return
			}

			try {
				await coursesService.publish(course.id)
				alert('Курс успешно опубликован')
				await this.loadCourses()
			} catch (error) {
				console.error('Ошибка публикации курса:', error)
				alert('Не удалось опубликовать курс')
			}
		},

		async unpublishCourse(course) {
			if (!confirm(`Снять с публикации курс "${course.title}"?`)) {
				return
			}

			try {
				await coursesService.unpublish(course.id)
				alert('Курс снят с публикации')
				await this.loadCourses()
			} catch (error) {
				console.error('Ошибка снятия с публикации:', error)
				alert('Не удалось снять курс с публикации')
			}
		},

		async deleteCourse(course) {
			if (!this.canDeleteCourses) {
				alert('У вас нет прав для удаления курсов')
				return
			}
			if (
				!confirm(
					`Вы уверены, что хотите удалить курс "${course.title}"? Это действие нельзя отменить.`
				)
			) {
				return
			}

			try {
				await coursesService.delete(course.id)
				alert('Курс успешно удален')
				await this.loadCourses()
			} catch (error) {
				console.error('Ошибка удаления курса:', error)
				alert('Не удалось удалить курс')
			}
		},
	},
}
</script>

<style scoped>
.course-management {
	padding: 32px;
	max-width: 1400px;
	margin: 0 auto;
}

.course-management__header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 32px;
}

.course-management__title {
	color: #252641;
	font-family: 'Poppins', Helvetica;
	font-size: 32px;
	font-weight: 700;
	margin: 0;
}

.course-management__add-btn {
	display: flex;
	align-items: center;
	gap: 8px;
	background-color: #0095ec;
	color: #ffffff;
	border: none;
	border-radius: 8px;
	padding: 12px 24px;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.3s ease;
}

.course-management__add-btn:hover {
	background-color: #0077be;
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(0, 149, 236, 0.3);
}

.course-management__add-icon {
	font-size: 20px;
}

.course-management__filters {
	display: flex;
	gap: 16px;
	margin-bottom: 24px;
	align-items: center;
	flex-wrap: wrap;
}

.course-management__save-status {
	margin-left: auto;
}

.course-management__search {
	flex: 1;
	padding: 12px 16px;
	border: 2px solid #e8e8e8;
	border-radius: 8px;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	transition: all 0.2s ease;
}

.course-management__search:focus {
	outline: none;
	border-color: #0095ec;
}

.course-management__select {
	padding: 12px 16px;
	border: 2px solid #e8e8e8;
	border-radius: 8px;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	cursor: pointer;
}

.course-management__table {
	background-color: #ffffff;
	border-radius: 12px;
	padding: 24px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	overflow-x: auto;
}

.course-management__loading {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 60px;
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
}

.spinner {
	width: 40px;
	height: 40px;
	border: 4px solid #f3f3f3;
	border-top: 4px solid #0095ec;
	border-radius: 50%;
	animation: spin 1s linear infinite;
	margin-bottom: 16px;
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

.table {
	width: 100%;
	border-collapse: collapse;
	min-width: 1000px;
}

.table th {
	text-align: left;
	padding: 12px;
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: 13px;
	font-weight: 500;
	border-bottom: 2px solid #e8e8e8;
	white-space: nowrap;
}

.table td {
	padding: 16px 12px;
	color: #252641;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	border-bottom: 1px solid #e8e8e8;
}

.table__course {
	display: flex;
	flex-direction: column;
	gap: 4px;
	max-width: 300px;
}

.table__course-subtitle {
	color: #636363;
	font-size: 12px;
}

.table__status {
	padding: 4px 12px;
	border-radius: 12px;
	font-size: 12px;
	font-weight: 500;
	white-space: nowrap;
}

.table__status--published {
	background-color: #d4edda;
	color: #155724;
}

.table__status--draft {
	background-color: #fff3cd;
	color: #856404;
}

.table__actions {
	display: flex;
	gap: 8px;
}

.table__action-btn {
	width: 36px;
	height: 36px;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: #f5f5f5;
	border: none;
	border-radius: 8px;
	font-size: 16px;
	cursor: pointer;
	transition: all 0.2s ease;
}

.table__action-btn:disabled {
	opacity: 0.4;
	cursor: not-allowed;
}

.table__action-btn:hover {
	background-color: #e8e8e8;
}

.table__action-btn--publish:hover {
	background-color: #d4edda;
}

.table__action-btn--unpublish:hover {
	background-color: #fff3cd;
}

.table__action-btn--delete:hover {
	background-color: #f8d7da;
}
</style>
